import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  onboardLookup,
  onboardRegister,
  onboardRequestAdvisor,
  selfServeCheckout,
} from "../api";

// Pre-registration 3-step wizard for ai.qada.io:
//   1. orgnr lookup + confirmation      (anonymous)
//   2. tier selection                   (anonymous)
//   3. account creation                 (anonymous, atomic)
//
// After successful register the wizard navigates to
// /ai/deal/<workspace_id>/setup so Sigge can start collecting the
// remaining onboarding facts via chat.
//
// The self-serve tier triggers Stripe Checkout AFTER account
// creation — pre-registration Stripe redirects can't reconcile with
// a workspace that doesn't exist yet. If Stripe isn't configured
// (dev), we skip the checkout call and land directly on the setup
// screen; the tier is already saved on the workspace.
export default function Onboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Two entry points:
  //   - Fresh visit → step 1 (lookup)
  //   - Return from Stripe (?success=true&ws=<id>) → jump to /ai/deal/<id>/setup
  useEffect(() => {
    if (searchParams.get("success") === "true") {
      const wsId = searchParams.get("ws");
      if (wsId) {
        navigate(`/deal/${wsId}/setup`, { replace: true });
      }
    }
  }, [searchParams, navigate]);

  const [step, setStep] = useState(1);
  const [company, setCompany] = useState(null); // lookup result
  const [tier, setTier] = useState(null);

  // Cross-step error banner
  const [error, setError] = useState(null);

  const goToStep = (n) => {
    setError(null);
    setStep(n);
  };

  return (
    <div className="qai-onboard-wrap">
      <div className="qai-onboard-brand">
        <span className="qai-brand-mark">Q</span>
        <span>Qada AI</span>
      </div>

      <div className="qai-onboard-card">
        <StepProgress step={step} />

        {error && <div className="qai-error qai-onboard-err">{error}</div>}

        {step === 1 && (
          <StepLookup
            initialOrgnr={company?.orgnr || ""}
            onConfirm={(payload) => {
              setCompany(payload);
              goToStep(2);
            }}
            onError={setError}
          />
        )}

        {step === 2 && company && (
          <StepTier
            company={company}
            onSelectPaid={(chosen) => {
              setTier(chosen);
              goToStep(3);
            }}
            onAdvisorSent={() => {
              // Broker/team lead captured — show acknowledgement,
              // no further navigation. User can hit "back" to try
              // a different path.
              setError(null);
              setStep(2.5);
            }}
            onError={setError}
          />
        )}

        {step === 2.5 && (
          <div className="qai-onboard-ack">
            <h2>Tack — vi hör av oss inom 24 timmar.</h2>
            <p>
              En rådgivare läser din förfrågan och kontaktar dig via
              mejl med nästa steg.
            </p>
            <button
              type="button"
              className="qai-onboard-secondary"
              onClick={() => goToStep(2)}
            >
              Tillbaka
            </button>
          </div>
        )}

        {step === 3 && company && tier && (
          <StepRegister
            company={company}
            tier={tier}
            onRegistered={async (result) => {
              // If the tier requires Stripe (self_serve), redirect
              // out. Otherwise land straight in the deal setup.
              if (tier === "self_serve" && result.workspace_slug) {
                try {
                  const co = await selfServeCheckout(result.workspace_slug);
                  if (co?.checkout_url) {
                    window.location.assign(co.checkout_url);
                    return;
                  }
                } catch (e) {
                  // Stripe unreachable — proceed to setup so the demo
                  // isn't blocked on payments. Backend has already
                  // recorded tier=self_serve on the workspace.
                }
              }
              navigate(`/deal/${result.workspace_id}/setup`, {
                replace: true,
              });
            }}
            onError={setError}
          />
        )}
      </div>
    </div>
  );
}

// ── Progress bar ────────────────────────────────────────────────────

function StepProgress({ step }) {
  const items = [
    { n: 1, label: "Bolag" },
    { n: 2, label: "Plan" },
    { n: 3, label: "Konto" },
  ];
  const current = Math.floor(step);
  return (
    <div className="qai-onboard-steps">
      {items.map((it) => {
        let cls = "qai-onboard-step";
        if (current === it.n) cls += " qai-onboard-step-active";
        else if (current > it.n) cls += " qai-onboard-step-done";
        return (
          <div key={it.n} className={cls}>
            <span className="qai-onboard-step-num">{it.n}</span>
            <span className="qai-onboard-step-label">{it.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Step 1: orgnr lookup ────────────────────────────────────────────

function StepLookup({ initialOrgnr, onConfirm, onError }) {
  const [orgnr, setOrgnr] = useState(initialOrgnr);
  const [lookup, setLookup] = useState(null); // result from POST /lookup/
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    onError(null);
    setLoading(true);
    try {
      const data = await onboardLookup(orgnr);
      setLookup(data);
    } catch (err) {
      const detail = err?.body?.error || err?.body?.detail || err.message;
      onError(detail || "Kunde inte hitta bolaget.");
      setLookup(null);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setLookup(null);
    onError(null);
  };

  if (lookup) {
    return (
      <div className="qai-onboard-body">
        <h1>Stämmer detta?</h1>
        <div className="qai-onboard-company">
          <div className="qai-onboard-company-name">{lookup.name}</div>
          <div className="qai-onboard-company-sub">
            Org.nr {lookup.orgnr}
            {lookup.sni_label ? ` · ${lookup.sni_label}` : ""}
          </div>
          <div className="qai-onboard-stats">
            {lookup.revenue_msek != null && (
              <div className="qai-onboard-stat">
                <span className="qai-onboard-stat-label">Omsättning</span>
                <span className="qai-onboard-stat-value">
                  {lookup.revenue_msek} MSEK
                </span>
              </div>
            )}
            {lookup.employees != null && (
              <div className="qai-onboard-stat">
                <span className="qai-onboard-stat-label">Anställda</span>
                <span className="qai-onboard-stat-value">
                  {lookup.employees}
                </span>
              </div>
            )}
            {lookup.founded_year != null && (
              <div className="qai-onboard-stat">
                <span className="qai-onboard-stat-label">Grundat</span>
                <span className="qai-onboard-stat-value">
                  {lookup.founded_year}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="qai-onboard-actions">
          <button
            type="button"
            className="qai-onboard-secondary"
            onClick={reset}
          >
            Nej, prova igen
          </button>
          <button
            type="button"
            className="qai-onboard-primary"
            onClick={() => onConfirm(lookup)}
          >
            Ja, det stämmer
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="qai-onboard-body" onSubmit={submit}>
      <h1>Vilket bolag vill du sälja?</h1>
      <p>Ange organisationsnumret så hämtar vi bolagsuppgifterna.</p>
      <label className="qai-onboard-field">
        <span>Organisationsnummer</span>
        <input
          type="text"
          value={orgnr}
          onChange={(e) => setOrgnr(e.target.value)}
          placeholder="5561234567"
          autoFocus
          disabled={loading}
        />
      </label>
      <div className="qai-onboard-actions">
        <button
          type="submit"
          className="qai-onboard-primary"
          disabled={loading || !orgnr.trim()}
        >
          {loading ? "Hämtar…" : "Fortsätt"}
        </button>
      </div>
    </form>
  );
}

// ── Step 2: tier selection ──────────────────────────────────────────

const TIER_META = {
  self_serve: {
    title: "Self-serve",
    price: "9 900 kr engångsavgift",
    fee: "+ 1,5 % framgångsarvode",
    blurb: "Kör själv med Sigge som stöd hela vägen till avslut.",
    cta: "Kom igång",
  },
  broker: {
    title: "Dedicerad rådgivare",
    price: "3 % framgångsarvode",
    fee: "Ingen fast avgift",
    blurb: "En Qada-rådgivare styr processen tillsammans med dig.",
    cta: "Kontakta mig",
  },
  team: {
    title: "Dedicerat team",
    price: "49 900 kr / månad",
    fee: "+ 1,5 % framgångsarvode",
    blurb: "Ett M&A-team dedikerat till din affär från start till slut.",
    cta: "Kontakta oss",
  },
};

function StepTier({ company, onSelectPaid, onAdvisorSent, onError }) {
  const [expanded, setExpanded] = useState(null); // "broker" | "team" | null
  const [advisorEmail, setAdvisorEmail] = useState("");
  const [advisorName, setAdvisorName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submitAdvisor = async () => {
    onError(null);
    setSubmitting(true);
    try {
      await onboardRequestAdvisor({
        tier: expanded,
        name: advisorName,
        email: advisorEmail,
        orgnr: company.orgnr,
      });
      onAdvisorSent();
    } catch (err) {
      onError(err?.body?.error || err.message || "Kunde inte skicka.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="qai-onboard-body">
      <h1>Välj upplägg</h1>
      <p>Alla planer inkluderar Sigge, DD-rummet och köparmatchning.</p>

      <div className="qai-tier-grid">
        {["self_serve", "broker", "team"].map((key) => {
          const meta = TIER_META[key];
          const isPaid = key === "self_serve";
          const open = expanded === key;
          return (
            <div key={key} className="qai-tier-card">
              <div className="qai-tier-head">
                <div className="qai-tier-title">{meta.title}</div>
                <div className="qai-tier-price">{meta.price}</div>
                <div className="qai-tier-fee">{meta.fee}</div>
              </div>
              <div className="qai-tier-blurb">{meta.blurb}</div>
              {isPaid ? (
                <button
                  type="button"
                  className="qai-onboard-primary qai-tier-cta"
                  onClick={() => onSelectPaid(key)}
                >
                  {meta.cta}
                </button>
              ) : open ? (
                <div className="qai-tier-advisor">
                  <input
                    type="text"
                    value={advisorName}
                    onChange={(e) => setAdvisorName(e.target.value)}
                    placeholder="Namn"
                    disabled={submitting}
                  />
                  <input
                    type="email"
                    value={advisorEmail}
                    onChange={(e) => setAdvisorEmail(e.target.value)}
                    placeholder="E-post"
                    disabled={submitting}
                  />
                  <div className="qai-tier-advisor-actions">
                    <button
                      type="button"
                      className="qai-onboard-secondary"
                      onClick={() => setExpanded(null)}
                      disabled={submitting}
                    >
                      Avbryt
                    </button>
                    <button
                      type="button"
                      className="qai-onboard-primary"
                      onClick={submitAdvisor}
                      disabled={submitting || !advisorEmail.trim()}
                    >
                      {submitting ? "Skickar…" : "Skicka"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="qai-onboard-secondary qai-tier-cta"
                  onClick={() => {
                    setExpanded(key);
                    onError(null);
                  }}
                >
                  {meta.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 3: account creation ────────────────────────────────────────

function StepRegister({ company, tier, onRegistered, onError }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    onError(null);
    setSubmitting(true);
    try {
      const res = await onboardRegister({
        orgnr: company.orgnr,
        company_name: company.name,
        tier,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
      });
      if (!res?.workspace_id) {
        // Anti-enumeration branch: email already taken.
        onError(
          "Kontot finns redan. Logga in istället och fortsätt därifrån.",
        );
        setSubmitting(false);
        return;
      }
      await onRegistered(res);
    } catch (err) {
      onError(err?.body?.error || err.message || "Kunde inte skapa konto.");
      setSubmitting(false);
    }
  };

  return (
    <form className="qai-onboard-body" onSubmit={submit}>
      <h1>Skapa ditt konto</h1>
      <p>
        {company.name} · {TIER_META[tier].title}
      </p>
      <div className="qai-onboard-grid">
        <label className="qai-onboard-field">
          <span>Förnamn</span>
          <input
            type="text"
            value={form.first_name}
            onChange={update("first_name")}
            disabled={submitting}
            autoFocus
          />
        </label>
        <label className="qai-onboard-field">
          <span>Efternamn</span>
          <input
            type="text"
            value={form.last_name}
            onChange={update("last_name")}
            disabled={submitting}
          />
        </label>
      </div>
      <label className="qai-onboard-field">
        <span>E-post</span>
        <input
          type="email"
          value={form.email}
          onChange={update("email")}
          disabled={submitting}
          required
        />
      </label>
      <label className="qai-onboard-field">
        <span>Lösenord (minst 8 tecken)</span>
        <input
          type="password"
          value={form.password}
          onChange={update("password")}
          disabled={submitting}
          minLength={8}
          required
        />
      </label>
      <div className="qai-onboard-actions">
        <button
          type="submit"
          className="qai-onboard-primary"
          disabled={submitting || !form.email || form.password.length < 8}
        >
          {submitting ? "Skapar konto…" : "Skapa konto"}
        </button>
      </div>
    </form>
  );
}
